import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { StatusPill } from "components/admin";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildAdministrativeRegionCreateValues,
  buildAdministrativeRegionFields,
  buildAdministrativeRegionUpdateValues,
} from "pages/app/workspace/sections/forms/school-admin-section-forms";
import { workspaceApi } from "pages/app/workspace/services";
import { hasPortalAction } from "utils/portal-access";
import { ToolbarSelect } from "pages/app/workspace/sections/styles/workspace-section.styles";

export const AdministrativeRegionsSection: React.FC<{ portalAccess: any }> = ({ portalAccess }) => {
  const [countryId, setCountryId] = useState("");
  const countriesQuery = useQuery({
    queryKey: ["workspace-region-countries"],
    queryFn: () => workspaceApi.countries.list(),
  });
  const regionsQuery = useQuery({
    queryKey: ["workspace-regions", countryId],
    queryFn: () => workspaceApi.administrativeRegions.list({ country_id: countryId || undefined }),
  });
  const canManageRegions = hasPortalAction(portalAccess, "administrative_regions.manage");
  const regionFields = buildAdministrativeRegionFields({
    countries: countriesQuery.data || [],
    regions: regionsQuery.data || [],
  });

  return (
    <CrudSection
      buildCreateValues={() =>
        buildAdministrativeRegionCreateValues(countryId, countriesQuery.data || [])
      }
      buildUpdateValues={buildAdministrativeRegionUpdateValues}
      canCreate={canManageRegions}
      canEdit={canManageRegions}
      canRemove={canManageRegions}
      columns={[
        { label: "Region", accessor: (row) => row.name },
        { label: "Country", accessor: (row) => row.country_name || "Unknown" },
        { label: "Kind", accessor: (row) => <StatusPill value={row.kind} /> },
        { label: "Code", accessor: (row) => row.code || "N/A" },
      ]}
      createLabel="Add region"
      data={regionsQuery.data || []}
      description={
        canManageRegions
          ? "Manage administrative regions used by schools and other location-aware endpoints."
          : "Review administrative regions used by schools and other location-aware endpoints."
      }
      invalidateKeys={[["workspace-regions", countryId], ["workspace-region-countries"]]}
      isLoading={regionsQuery.isLoading}
      fields={regionFields}
      onCreate={(payload) => workspaceApi.administrativeRegions.create(payload)}
      onRemove={(row) => workspaceApi.administrativeRegions.remove(row.id)}
      onUpdate={(row, payload) => workspaceApi.administrativeRegions.update(row.id, payload)}
      title="Administrative Regions"
      toolbar={
        <ToolbarSelect onChange={(event) => setCountryId(event.target.value)} value={countryId}>
          <option value="">All countries</option>
          {(countriesQuery.data || []).map((country: any) => (
            <option key={country.id || country.name} value={country.id}>
              {country.name}
            </option>
          ))}
        </ToolbarSelect>
      }
    />
  );
};
